import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { AuthContext, useAuthProvider, useAuth } from './hooks/useAuth';
import { AppLayout } from './components/layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { LoadingFallback } from './components/LoadingFallback';
import type { ReactNode } from 'react';

// Lazy-loaded páginas — carregam sob demanda para reduzir bundle inicial
const ClientsPage = lazy(() => import('./pages/ClientsPage').then((m) => ({ default: m.ClientsPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const LeadsPage = lazy(() => import('./pages/LeadsPage').then((m) => ({ default: m.LeadsPage })));
const LeadDetailPage = lazy(() => import('./pages/LeadDetailPage').then((m) => ({ default: m.LeadDetailPage })));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage').then((m) => ({ default: m.CampaignsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AdminClientsPage = lazy(() => import('./pages/AdminClientsPage').then((m) => ({ default: m.AdminClientsPage })));
const AdminClientFormPage = lazy(() => import('./pages/AdminClientFormPage').then((m) => ({ default: m.AdminClientFormPage })));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const DataDeletionPage = lazy(() => import('./pages/DataDeletionPage').then((m) => ({ default: m.DataDeletionPage })));

function RequireSuperadmin({ children }: { children: ReactNode }) {
  const { isSuperadmin, loading } = useAuth();
  // Durante boot do auth, isSuperadmin defaulta para false. Sem checar
  // loading, refresh em /admin/* jogava superadmin pra "/" antes do
  // useAuth carregar.
  if (loading) return <LoadingFallback />;
  if (!isSuperadmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ErrorFallback() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui' }}>
      <h1>Algo deu errado</h1>
      <p>Um erro inesperado ocorreu. Tente recarregar a página.</p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '0.5rem 1rem', marginTop: '1rem', cursor: 'pointer' }}
      >
        Recarregar
      </button>
    </div>
  );
}

function App() {
  const auth = useAuthProvider();

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <AuthContext.Provider value={auth}>
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route element={<AppLayout />}>
              <Route path="/" element={<ClientsPage />} />
              <Route path="/clients/:slug/dashboard" element={<DashboardPage />} />
              <Route path="/clients/:slug/leads" element={<LeadsPage />} />
              <Route path="/clients/:slug/leads/:id" element={<LeadDetailPage />} />
              <Route path="/clients/:slug/campaigns" element={<CampaignsPage />} />
              <Route path="/clients/:slug/settings" element={<SettingsPage />} />
              <Route path="/admin/clients" element={<RequireSuperadmin><AdminClientsPage /></RequireSuperadmin>} />
              <Route path="/admin/clients/new" element={<RequireSuperadmin><AdminClientFormPage /></RequireSuperadmin>} />
              <Route path="/admin/clients/:id/edit" element={<RequireSuperadmin><AdminClientFormPage /></RequireSuperadmin>} />
              <Route path="/admin/users" element={<RequireSuperadmin><AdminUsersPage /></RequireSuperadmin>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthContext.Provider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
